/**
 * NORMALIZE TYPOGRAPHIC CHARACTERS
 *
 * Collapses common smart-punctuation variants down to their plain ASCII
 * equivalents. Same characters your users MEANT to type, but which their
 * OS auto-corrected into fancier Unicode forms:
 *
 *   ’ ‘  (U+2019, U+2018)  → '   (iOS "smart quotes" for apostrophes)
 *   ” “  (U+201D, U+201C)  → "   (iOS "smart quotes" for quotes)
 *   – —  (U+2013, U+2014)  → -   (en/em dashes → hyphen)
 *   …    (U+2026)           → ... (ellipsis)
 *
 * Why we need this:
 *   Firestore stores exact bytes, so "Lil’ Sweet" (typed on iOS) and
 *   "Lil' Sweet" (typed on Android) look identical to a human but are
 *   DIFFERENT strings — they hash differently, they don't equal each
 *   other, they show up as two separate entries in a brand's polish
 *   list. Normalizing to plain ASCII on save (or on merge) keeps the
 *   data uniform so downstream comparisons Just Work.
 *
 * NOT the same as slugify() — slugify strips these characters entirely.
 * This preserves them as their ASCII counterparts so the display name
 * still reads naturally ("Lil' Sweet", not "Lil Sweet").
 */
export function normalizeTypography(input: string): string {
  if (!input) return input;
  return input
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    // Strip zero-width unicode (ZWSP U+200B, ZWNJ U+200C, ZWJ U+200D,
    // BOM/ZWNBSP U+FEFF, WORD-JOINER U+2060). These are invisible copies
    // of a brand string — the classic "why won't 'Cracked' and 'Cracked'
    // merge" cause. Also collapse non-breaking space (U+00A0) to a
    // regular space so trim() below can catch trailing-NBSP cases.
    .replace(/[​-‍﻿⁠]/g, '')
    .replace(/ /g, ' ')
    .trim();
}
